import { useState } from 'react';
import { ResultPaginationResponseDto } from '@tiendanube/common';
import {
  getProductsOrderProductList,
  getProductsOrderRemainingProducts,
} from '../../themesService';
import {
  ProductsOrderListFiltersInterface,
  ProductsOrderRemainingFiltersInterface,
  SectionProductItemInterface,
} from '../../types';

const initialValue: ResultPaginationResponseDto<SectionProductItemInterface[]> =
  {
    pagination: {
      totalPages: 0,
      currentPage: 0,
      totalResults: 0,
      perPage: 50,
      nextPage: 1,
    },
    results: [],
  };

function useProductOrderList() {
  const [status, setStatus] = useState('idle');
  const [isLoadingRemainingProducts, setIsLoadingRemainingProducts] =
    useState(false);
  const [products, setProducts] =
    useState<ResultPaginationResponseDto<SectionProductItemInterface[]>>(
      initialValue,
    );

  const fetchProductOrderlist = async (
    filters: ProductsOrderListFiltersInterface,
    reset = false,
  ) => {
    try {
      setStatus('loading');
      const moreProducts = await getProductsOrderProductList(filters);
      setStatus('success');
      setProducts((current) => ({
        pagination: moreProducts.pagination,
        results: [
          ...(reset ? [] : current.results),
          ...moreProducts.results.map((product) => ({
            ...product,
            selected: false,
          })),
        ],
      }));
    } catch (_) {
      setStatus('error');
    }
  };

  const resetProducts = () => {
    setProducts(initialValue);
  };

  const fetchRemainingProducts = async (
    filters: ProductsOrderRemainingFiltersInterface,
  ) => {
    try {
      setIsLoadingRemainingProducts(true);
      const remainingProducts = await getProductsOrderRemainingProducts(
        filters,
      );
      const productsWithSelection = remainingProducts.map((product) => ({
        ...product,
        selected: false,
      }));

      const updatedResults = [...products.results, ...productsWithSelection];

      setProducts((current) => {
        const pagination = {
          ...current.pagination,
          nextPage: null,
        };
        return {
          pagination,
          results: [...current.results, ...productsWithSelection],
        };
      });
      return updatedResults; // Return ALL products (existing + new)
    } finally {
      setIsLoadingRemainingProducts(false);
    }
  };

  return {
    products,
    status,
    isLoadingRemainingProducts,
    setProducts,
    resetProducts,
    fetchProductOrderlist,
    fetchRemainingProducts,
  };
}

export default useProductOrderList;
